/**
 * The festival weekend, in one place.
 *
 * **Bump these two dates on rollover.** They are the only client-side statement
 * of when OHC happens; `OHC_YEAR` in server/collections/events.ts is the server
 * half of the same job.
 *
 * Asana O2: the Sites filter carried its own copy of this as a hardcoded table,
 * and at the 2026 rollover it still read the 2025 weekend — "Saturday, Oct. 18"
 * and "Sunday, Oct. 19" when the festival is Oct 17-18. Grace reported it four
 * days after launch, on a High priority ticket: anyone trusting the filter would
 * have planned the wrong days.
 *
 * useEventDay hit the identical bug and fixed it the same way. Its table was
 *
 *     { num: 6, label: 'Saturday', date: 18 }
 *
 * and that shape is the trap: weekday and number stored separately, so a stale
 * number still reads as a coherent label. Storing the date alone and deriving
 * the rest means they cannot drift apart, and the test asserts the pairing.
 */
export interface FestivalDay {
    /** ISO date, the single source for both the weekday and the number. */
    date: string;
    /** The Airtable hours field for that day — what the filter actually queries. */
    field: 'Saturday Hours' | 'Sunday Hours';
}

export const FESTIVAL_DAYS: readonly FestivalDay[] = [
    { date: '2026-10-17', field: 'Saturday Hours' },
    { date: '2026-10-18', field: 'Sunday Hours' },
];

/**
 * "Saturday, Oct. 17" — weekday, month and number all read off the one date.
 *
 * UTC components throughout, matching useEventDay and Templates/Events/page.vue:
 * a bare `2026-10-17` parses as UTC midnight, and rendering it in
 * America/Chicago lands on the 16th (the shape of Asana O2-34).
 *
 * The trailing period suits the abbreviated months; OHC is an October festival,
 * so the one case that would read oddly (`May.`) cannot arise.
 */
export function festivalDayLabel(isoDate: string): string {
    const date = new Date(`${isoDate}T00:00:00Z`);
    const weekday = date.toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'long' });
    const month = date.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short' });
    return `${weekday}, ${month}. ${date.getUTCDate()}`;
}
